import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { toast } from '../components/toast';
import { getEmailSendingLogs } from '../services/api';
import ComposeEmailPopup from '../components/email-sender/ComposeEmailPopup';
import EmailViewDialog from '../components/email-sender/EmailViewDialog';

dayjs.extend(isToday);

const STATUS_COLORS = {
  Success: 'success',
  Failure: 'error',
};

function formatDate(ts) {
  if (!ts) return '—';
  const d = dayjs(ts);
  return d.isToday() ? d.format('h:mm A') : d.format('MMM D');
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function GmailRow({ log, onClick }) {
  const recipients = log.recipient_emails || '—';
  const subject = log.subject || '(no subject)';
  const snippet = stripHtml(log.body);
  const date = formatDate(log.created_at);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 0,
        minHeight: 48,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        gap: 1.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Recipients */}
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ minWidth: 180, maxWidth: 180, flexShrink: 0 }}
      >
        {recipients}
      </Typography>

      {/* Subject + snippet */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 0.75, overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flexShrink: 0, maxWidth: 280 }}>
          {subject}
        </Typography>
        {snippet && (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
            — {snippet}
          </Typography>
        )}
      </Box>

      {/* Status chip */}
      <Chip
        label={log.status}
        size="small"
        color={STATUS_COLORS[log.status] ?? 'default'}
        variant="outlined"
        sx={{ flexShrink: 0, fontSize: 11, height: 22 }}
      />

      {/* Date */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flexShrink: 0, minWidth: 52, textAlign: 'right', fontSize: 12 }}
      >
        {date}
      </Typography>
    </Box>
  );
}

export default function EmailSenderPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async (currentPage, currentPageSize) => {
    setLoading(true);
    try {
      const res = await getEmailSendingLogs(currentPage + 1, currentPageSize);
      setLogs(res.data.results ?? []);
      setTotalCount(res.data.count ?? 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, rowsPerPage);
  }, [fetchLogs, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSent = () => {
    setPage(0);
    fetchLogs(0, rowsPerPage);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Email Sender</title>
      </Helmet>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Email Sender</Typography>
        <Button
          variant="contained"
          disableElevation
          startIcon={<EditIcon />}
          onClick={() => setComposeOpen(true)}
        >
          New Email
        </Button>
      </Stack>

      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No emails sent yet.</Typography>
          </Box>
        ) : (
          logs.map((log) => <GmailRow key={log.id} log={log} onClick={() => setSelectedLog(log)} />)
        )}

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>

      {composeOpen && (
        <ComposeEmailPopup onClose={() => setComposeOpen(false)} onSent={handleSent} />
      )}

      {selectedLog && (
        <EmailViewDialog log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </>
  );
}

